import type {
  DecisionAgentNumericMetricContract,
  DecisionAgentRequiredFor,
  DecisionPackContract,
  FormulaGovernanceExemptionContract,
  MonitoringContract,
  ProofPolicyContract,
  RequiredStateContract,
  ScenarioContract,
} from './decision-agent-platform';

type FormulaRefValidationContext = {
  decisionPack: DecisionPackContract;
  errors: string[];
  // #7254 — an approved-formula output metric that is neither a decisionPack
  // metric nor a monitoring output may still be legitimate: a formula wired
  // ONLY into the manager/ask_helix read path (never exported to a decision
  // pack, never evaluated on a monitoring cadence). Its outputs are accepted
  // here ONLY if the module's OWN manifest already declares them as governed
  // numeric bindings it produces (governedCapability.numericBindingsProduced)
  // AND the ref's requiredFor names 'ask_helix' — i.e. the metric is already
  // fence-admissible through the module's own governed-capability surface;
  // this does not touch the numericBindingIsEngineBound render-time fence.
  // First landed with financeIncomeMarginTarget's formula ref (#7254, PR #7364
  // finding #2), reverted for disclosure during that PR's split, restored here
  // with this comment and the named test in decision-agent-formula-ref-validation.spec.ts.
  governedNumericBindings: readonly string[];
  moduleId: string;
  monitoring: MonitoringContract;
  proofPolicy: ProofPolicyContract;
  requiredState: readonly RequiredStateContract[];
  scenarios: readonly ScenarioContract[];
  validateRequiredFor: (values: readonly DecisionAgentRequiredFor[], label: string, errors: string[]) => void;
};

type FormulaMetricState = {
  coveredDecisionMetrics: Set<string>;
  decisionMetricKeys: Set<string>;
  evidenceRefs: Set<string>;
  governedNumericBindingKeys: Set<string>;
  monitoringOutputKeys: Set<string>;
  scenarioMetricKeys: Set<string>;
};

type FormulaOutputValidationInput = {
  errors: string[];
  index: number;
  metricKey: string;
  requiredFor: Set<string>;
  state: FormulaMetricState;
};

const SOURCE_OWNED_EVIDENCE_ROLLUP_METRICS: Readonly<
  Record<string, Readonly<Record<string, readonly string[]>>>
> = {
  'org-analysis-optimization': {
    org_cost_sub_lever_net_run_rate_contribution_usd: [
      'org_cost_sub_lever_buildup',
      'position_cost_roster',
      'initiative_capacity_binding',
    ],
  },
  'workforce-reduction': {
    workforce_reduction_net_ebitda_opportunity_usd: [
      'org_state_as_is',
      'position_cost_roster',
      'people_tenure',
      'severance_policy',
      'initiative_capacity_binding',
      'workforce_legal_gate',
    ],
    workforce_reduction_run_rate_savings_usd: ['org_state_as_is', 'position_cost_roster'],
    workforce_reduction_one_time_severance_usd: [
      'people_tenure',
      'severance_policy',
      'workforce_legal_gate',
    ],
  },
  'churn-reduction': {
    churn_event_count: ['churn_events'],
    revenue_at_risk_usd: ['churn_events'],
    cohort_retention_rate_pct: ['cohort_retention'],
  },
};

export function validateApprovedFormulaRefs(context: FormulaRefValidationContext): void {
  const state = createFormulaMetricState(context);
  const refs = context.proofPolicy.approvedFormulaRefs;
  if (!hasItems(refs)) {
    validateAbsentFormulaRefs(context, state);
    return;
  }
  if (context.proofPolicy.formulaGovernanceExemption !== undefined) {
    context.errors.push('proofPolicy.formulaGovernanceExemption must not be set when approvedFormulaRefs are present.');
  }
  refs.forEach((ref, index) => validateFormulaRef(ref, index, context, state));
  validateDecisionMetricCoverage(context.errors, state);
}

function createFormulaMetricState(context: FormulaRefValidationContext): FormulaMetricState {
  const sourceOwned = resolveSourceOwnedEvidenceRollupMetricKeys(
    context.moduleId, context.decisionPack.requiredMetrics ?? [], context.proofPolicy, context.errors,
  );
  return {
    coveredDecisionMetrics: new Set<string>(),
    decisionMetricKeys: new Set((context.decisionPack.requiredMetrics ?? []).filter((metric) => !sourceOwned.has(metric.metricKey)).map((metric) => metric.metricKey)),
    evidenceRefs: new Set([...(context.proofPolicy.requiredEvidenceRefs ?? []), ...context.requiredState.map((state) => state.stateKey)]),
    governedNumericBindingKeys: new Set(context.governedNumericBindings),
    monitoringOutputKeys: new Set(context.monitoring.outputMetrics ?? []),
    scenarioMetricKeys: new Set((context.scenarios ?? []).flatMap((scenario) => scenario.outputMetrics ?? [])),
  };
}

function validateAbsentFormulaRefs(context: FormulaRefValidationContext, state: FormulaMetricState): void {
  if (state.decisionMetricKeys.size > 0) {
    validateFormulaGovernanceExemption(context.moduleId, context.proofPolicy.formulaGovernanceExemption, context.errors);
  }
}

function validateFormulaRef(
  ref: NonNullable<ProofPolicyContract['approvedFormulaRefs']>[number],
  index: number,
  context: FormulaRefValidationContext,
  state: FormulaMetricState,
): void {
  validateFormulaRefMetadata(ref, index, context.errors, context.validateRequiredFor);
  const requiredFor = new Set(ref.requiredFor ?? []);
  ref.outputMetrics?.forEach((metricKey) => validateFormulaOutput({ errors: context.errors, index, metricKey, requiredFor, state }));
  ref.evidenceRefs?.forEach((evidenceRef) => validateFormulaEvidenceRef(evidenceRef, index, context.errors, state));
}

function validateFormulaRefMetadata(
  ref: NonNullable<ProofPolicyContract['approvedFormulaRefs']>[number], index: number, errors: string[],
  validateRequiredFor: FormulaRefValidationContext['validateRequiredFor'],
): void {
  requireNonBlank(ref.formulaId, `proofPolicy.approvedFormulaRefs[${index}].formulaId`, errors);
  requireNonBlank(ref.formulaVersion, `proofPolicy.approvedFormulaRefs[${index}].formulaVersion`, errors);
  if (!isBlank(ref.formulaVersion) && !/^[0-9]+\.[0-9]+\.[0-9]+$/.test(ref.formulaVersion)) {
    errors.push(`proofPolicy.approvedFormulaRefs[${index}].formulaVersion must be semver.`);
  }
  if (!hasItems(ref.requiredFor)) {
    errors.push(`proofPolicy.approvedFormulaRefs[${index}].requiredFor must include at least one runtime use.`);
  } else {
    validateRequiredFor(ref.requiredFor, `proofPolicy.approvedFormulaRefs[${index}]`, errors);
  }
  if (!hasItems(ref.outputMetrics)) {
    errors.push(`proofPolicy.approvedFormulaRefs[${index}].outputMetrics must include at least one output metric or monitoring output.`);
  }
  if (!hasItems(ref.evidenceRefs)) {
    errors.push(`proofPolicy.approvedFormulaRefs[${index}].evidenceRefs must include at least one required evidence ref.`);
  }
}

function validateFormulaOutput({ errors, index, metricKey, requiredFor, state }: FormulaOutputValidationInput): void {
  const label = `proofPolicy.approvedFormulaRefs[${index}].outputMetrics`;
  if (state.decisionMetricKeys.has(metricKey)) {
    if (!requiredFor.has('decision_pack')) errors.push(`${label} references decisionPack metric ${metricKey} but requiredFor does not include decision_pack.`);
    else state.coveredDecisionMetrics.add(metricKey);
    if (state.scenarioMetricKeys.has(metricKey) && !requiredFor.has('scenario')) errors.push(`${label} references scenario metric ${metricKey} but requiredFor does not include scenario.`);
    return;
  }
  if (state.monitoringOutputKeys.has(metricKey)) {
    if (!requiredFor.has('monitoring')) errors.push(`${label} references monitoring output ${metricKey} but requiredFor does not include monitoring.`);
    return;
  }
  if (state.governedNumericBindingKeys.has(metricKey)) {
    if (!requiredFor.has('ask_helix')) errors.push(`${label} references Ask Helix output ${metricKey} but requiredFor does not include ask_helix.`);
    return;
  }
  errors.push(`${label} references unknown approved formula output metric ${metricKey}.`);
}

function validateFormulaEvidenceRef(evidenceRef: string, index: number, errors: string[], state: FormulaMetricState): void {
  if (!state.evidenceRefs.has(evidenceRef)) {
    errors.push(`proofPolicy.approvedFormulaRefs[${index}].evidenceRefs references evidence ref ${evidenceRef} that is neither a proofPolicy requiredEvidenceRef nor a requiredState stateKey.`);
  }
}

function validateDecisionMetricCoverage(errors: string[], state: FormulaMetricState): void {
  state.decisionMetricKeys.forEach((metricKey) => {
    if (!state.coveredDecisionMetrics.has(metricKey)) {
      errors.push(`proofPolicy.approvedFormulaRefs must bind decisionPack.requiredMetrics metric ${metricKey}.`);
    }
  });
}

function resolveSourceOwnedEvidenceRollupMetricKeys(moduleId: string, metrics: readonly DecisionAgentNumericMetricContract[], proofPolicy: ProofPolicyContract, errors: string[]): Set<string> {
  const allowedForModule = SOURCE_OWNED_EVIDENCE_ROLLUP_METRICS[moduleId] ?? {};
  const requiredEvidenceRefs = new Set(proofPolicy.requiredEvidenceRefs ?? []);
  const allowedMetricKeys = new Set<string>();
  metrics.forEach((metric) => {
    if (metric.source !== 'source-owned-evidence-rollup') return;
    const requiredRefs = allowedForModule[metric.metricKey];
    if (!requiredRefs) {
      errors.push(`decisionPack.requiredMetrics metric ${metric.metricKey} cannot use source-owned-evidence-rollup for module ${moduleId}.`);
      return;
    }
    const missingRefs = requiredRefs.filter((evidenceRef) => !requiredEvidenceRefs.has(evidenceRef));
    if (missingRefs.length > 0) {
      errors.push(`decisionPack.requiredMetrics metric ${metric.metricKey} source-owned-evidence-rollup requires proofPolicy.requiredEvidenceRefs ${missingRefs.join(', ')}.`);
      return;
    }
    allowedMetricKeys.add(metric.metricKey);
  });
  return allowedMetricKeys;
}

function validateFormulaGovernanceExemption(moduleId: string, exemption: FormulaGovernanceExemptionContract | undefined, errors: string[]): void {
  if (!exemption) {
    errors.push('proofPolicy.approvedFormulaRefs must include at least one approved formula ref when decisionPack.requiredMetrics are declared, unless formulaGovernanceExemption is explicitly tracked for this module.');
    return;
  }
  requireNonBlank(exemption.trackingId, 'proofPolicy.formulaGovernanceExemption.trackingId', errors);
  requireNonBlank(exemption.reason, 'proofPolicy.formulaGovernanceExemption.reason', errors);
  requireNonBlank(exemption.deletionCriteria, 'proofPolicy.formulaGovernanceExemption.deletionCriteria', errors);
  if (exemption.activationBlockedUntilResolved !== true) errors.push('proofPolicy.formulaGovernanceExemption.activationBlockedUntilResolved must be true.');
  const allowed = ALLOWED_FORMULA_GOVERNANCE_EXEMPTIONS[moduleId];
  if (!allowed) {
    errors.push(`proofPolicy.formulaGovernanceExemption is not allowed for module ${moduleId}.`);
    return;
  }
  if (exemption.trackingId !== allowed.trackingId) errors.push(`proofPolicy.formulaGovernanceExemption.trackingId for module ${moduleId} must be ${allowed.trackingId}.`);
  if (!isBlank(exemption.deletionCriteria) && !exemption.deletionCriteria.includes(allowed.deletionCriteriaIncludes)) errors.push(`proofPolicy.formulaGovernanceExemption.deletionCriteria for module ${moduleId} must name ${allowed.deletionCriteriaIncludes}.`);
}

const ALLOWED_FORMULA_GOVERNANCE_EXEMPTIONS: Readonly<Record<string, { trackingId: string; deletionCriteriaIncludes: string }>> = {
  finance: { trackingId: 'helix-86hg', deletionCriteriaIncludes: 'finance approved formula definitions' },
};

function hasItems(value: readonly unknown[] | undefined): value is readonly unknown[] {
  return Array.isArray(value) && value.length > 0;
}

function isBlank(value: string | undefined): boolean {
  return value === undefined || value.trim() === '';
}

function requireNonBlank(value: string | undefined, label: string, errors: string[]): void {
  if (isBlank(value)) errors.push(`${label} is required.`);
}
